import React, { useState } from "react";
import { ArrowLeft, Plus, Minus, Upload } from "lucide-react";
import { Button } from "../UI/Button";
import TextInput from "../UI/TextInput";
import type { CreateOrderRequest, CustomOrderOption } from "../../../domain/types/orders";

interface CompanyMember {
  id: number;
  name: string;
  email: string;
  mobile: string;
}

interface CustomOrderCreateFormProps {
  companyId: string;
  companyMembers: CompanyMember[];
  onBack: () => void;
  onSubmit: (orderData: CreateOrderRequest, file: File | null, selectedMemberId: number | null) => Promise<void>;
}

export const CustomOrderCreateForm: React.FC<CustomOrderCreateFormProps> = ({
  companyId,
  companyMembers,
  onBack,
  onSubmit,
}) => {
  // 기본 정보
  const [orderName, setOrderName] = useState("");
  const [customProductName, setCustomProductName] = useState("");
  const [productPrice, setProductPrice] = useState<number>(0);
  const [productCount, setProductCount] = useState<number>(1);

  // 커스텀 옵션
  const [customOptions, setCustomOptions] = useState<CustomOrderOption[]>([]);

  // 주문자 정보
  const [isGuestOrder, setIsGuestOrder] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [memberSearchKeyword, setMemberSearchKeyword] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestMobile, setGuestMobile] = useState("");

  // 추가 정보
  const [initialStatus, setInitialStatus] = useState<"BEFORE_PAYMENT" | "RECEIVED">("BEFORE_PAYMENT");
  const [memo, setMemo] = useState("");
  const [orderFile, setOrderFile] = useState<File | null>(null);

  // 금액없이 생성 옵션
  const [withoutPrice, setWithoutPrice] = useState(false);

  // 커스텀 옵션 추가
  const handleAddOption = () => {
    setCustomOptions([
      ...customOptions,
      {
        optionName: "",
        optionPrice: 0,
        optionCount: 1,
      },
    ]);
  };

  // 커스텀 옵션 삭제
  const handleRemoveOption = (index: number) => {
    setCustomOptions(customOptions.filter((_, i) => i !== index));
  };

  // 커스텀 옵션 수정
  const handleUpdateOption = (index: number, field: keyof CustomOrderOption, value: string | number) => {
    const updated = [...customOptions];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setCustomOptions(updated);
  };

  // 총 금액 계산
  const calculateTotal = () => {
    const productTotal = productPrice * productCount;
    const optionsTotal = customOptions.reduce((sum, opt) => sum + opt.optionPrice * opt.optionCount, 0);
    return productTotal + optionsTotal;
  };

  // 회원 필터링
  const filteredMembers = React.useMemo(() => {
    if (!memberSearchKeyword || memberSearchKeyword.trim() === "") {
      return companyMembers.slice(0, 5);
    }
    const keyword = memberSearchKeyword.toLowerCase();
    return companyMembers.filter(
      (m) => m.name.toLowerCase().includes(keyword) || m.email.toLowerCase().includes(keyword)
    );
  }, [companyMembers, memberSearchKeyword]);

  // 제출
  const handleSubmit = async () => {
    if (!customProductName || productCount <= 0) {
      alert("상품명, 수량을 입력해주세요.");
      return;
    }

    if (!withoutPrice && productPrice <= 0) {
      alert("상품 가격을 입력해주세요.");
      return;
    }

    if (!isGuestOrder && !selectedMemberId) {
      alert("회원을 선택해주세요.");
      return;
    }

    if (isGuestOrder && !guestName) {
      alert("비회원 이름을 입력해주세요.");
      return;
    }

    // 금액없이 모드일 경우 가격을 0으로 설정
    const finalProductPrice = withoutPrice ? 0 : productPrice;
    const finalCustomOptions = withoutPrice
      ? customOptions.map((opt) => ({ ...opt, optionPrice: 0 }))
      : customOptions;

    const orderData: CreateOrderRequest = {
      name: orderName || customProductName,
      customOrderInfo: {
        customProductName,
        productPrice: finalProductPrice,
        productCount,
        customOptions: finalCustomOptions.length > 0 ? finalCustomOptions : undefined,
      },
      initialStatus,
      purchaserId: isGuestOrder ? undefined : selectedMemberId || undefined,
      guestInfo: isGuestOrder
        ? {
            name: guestName,
            email: guestEmail || undefined,
            mobile: guestMobile || undefined,
          }
        : undefined,
      memo: memo || undefined,
    };

    await onSubmit(orderData, orderFile, selectedMemberId);
  };

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="w-full">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">커스텀 주문 생성</h1>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={withoutPrice}
              onChange={(e) => setWithoutPrice(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">금액없이 생성</span>
          </label>
        </div>

        <div className="space-y-6 text-left">
          {/* 기본 정보 카드 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 text-left">기본 정보</h2>

            {/* 주문명 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">주문명 (선택)</label>
              <TextInput
                value={orderName}
                onChange={(e) => setOrderName(e.target.value)}
                placeholder="비워두면 상품명이 사용됩니다"
              />
            </div>

            {/* 상품명 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                상품명 <span className="text-red-500">*</span>
              </label>
              <TextInput
                value={customProductName}
                onChange={(e) => setCustomProductName(e.target.value)}
                placeholder="커스텀 상품명을 입력하세요"
              />
            </div>

            {/* 상품 단가 */}
            {!withoutPrice && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  상품 단가 <span className="text-red-500">*</span>
                </label>
                <TextInput
                  type="number"
                  value={productPrice}
                  onChange={(e) => setProductPrice(Number(e.target.value))}
                  placeholder="0"
                />
              </div>
            )}

            {/* 상품 수량 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                상품 수량 <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center border border-gray-300 rounded-lg w-40">
                <button
                  type="button"
                  className="p-3 hover:bg-gray-100 transition"
                  onClick={() => setProductCount(Math.max(1, productCount - 1))}
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  min="1"
                  value={productCount}
                  onChange={(e) => setProductCount(Math.max(1, Number(e.target.value)))}
                  className="flex-1 text-center border-0 focus:outline-none font-medium"
                />
                <button
                  type="button"
                  className="p-3 hover:bg-gray-100 transition"
                  onClick={() => setProductCount(productCount + 1)}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* 커스텀 옵션 카드 */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 text-left">커스텀 옵션 (선택)</h2>
              <Button onClick={handleAddOption} variant="outline" className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                옵션 추가
              </Button>
            </div>

            {customOptions.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                추가 옵션이 필요한 경우 "옵션 추가" 버튼을 눌러주세요
              </p>
            ) : (
              <div className="space-y-4">
                {customOptions.map((option, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-gray-700">옵션 {index + 1}</span>
                      <button
                        onClick={() => handleRemoveOption(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        삭제
                      </button>
                    </div>

                    <div className="space-y-3">
                      {/* 옵션명 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">옵션명</label>
                        <TextInput
                          value={option.optionName}
                          onChange={(e) => handleUpdateOption(index, "optionName", e.target.value)}
                          placeholder="예: 특급 배송, 프린트 추가"
                        />
                      </div>

                      <div className={withoutPrice ? "" : "grid grid-cols-2 gap-3"}>
                        {/* 옵션 단가 */}
                        {!withoutPrice && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">옵션 단가</label>
                            <TextInput
                              type="number"
                              value={option.optionPrice}
                              onChange={(e) => handleUpdateOption(index, "optionPrice", Number(e.target.value))}
                              placeholder="0"
                            />
                          </div>
                        )}

                        {/* 옵션 수량 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">옵션 수량</label>
                          <TextInput
                            type="number"
                            value={option.optionCount}
                            onChange={(e) => handleUpdateOption(index, "optionCount", Number(e.target.value))}
                            placeholder="1"
                          />
                        </div>
                      </div>

                      {/* 옵션 소계 */}
                      {!withoutPrice && (
                        <div className="text-sm text-gray-600 text-right">
                          소계: ₩{(option.optionPrice * option.optionCount).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 주문자 정보 카드 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 text-left">주문자 정보</h2>

            {/* 회원/비회원 선택 */}
            <div className="mb-4">
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    checked={!isGuestOrder}
                    onChange={() => {
                      setIsGuestOrder(false);
                      setGuestName("");
                      setGuestEmail("");
                      setGuestMobile("");
                    }}
                    className="mr-2 w-4 h-4"
                  />
                  <span className="text-sm font-medium">회원 주문</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    checked={isGuestOrder}
                    onChange={() => {
                      setIsGuestOrder(true);
                      setSelectedMemberId(null);
                      setMemberSearchKeyword("");
                    }}
                    className="mr-2 w-4 h-4"
                  />
                  <span className="text-sm font-medium">비회원 주문</span>
                </label>
              </div>
            </div>

            {/* 회원 선택 */}
            {!isGuestOrder && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  회원 선택 <span className="text-red-500">*</span>
                </label>

                {/* 선택된 회원 표시 */}
                {selectedMemberId && companyMembers.find((m) => m.id === selectedMemberId) && (
                  <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">
                          {companyMembers.find((m) => m.id === selectedMemberId)?.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {companyMembers.find((m) => m.id === selectedMemberId)?.email}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedMemberId(null);
                          setMemberSearchKeyword("");
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        변경
                      </button>
                    </div>
                  </div>
                )}

                {/* 검색 입력 */}
                {!selectedMemberId && (
                  <div className="relative">
                    <TextInput
                      value={memberSearchKeyword}
                      onChange={(e) => setMemberSearchKeyword(e.target.value)}
                      placeholder="회원 이름 또는 이메일로 검색"
                    />
                    {memberSearchKeyword && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredMembers.length > 0 ? (
                          filteredMembers.map((member) => (
                            <div
                              key={member.id}
                              className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition"
                              onClick={() => {
                                setSelectedMemberId(member.id);
                                setMemberSearchKeyword("");
                              }}
                            >
                              <div className="font-medium text-gray-900">{member.name}</div>
                              <div className="text-sm text-gray-500">{member.email}</div>
                              {member.mobile && <div className="text-xs text-gray-400">{member.mobile}</div>}
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">검색 결과가 없습니다.</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 비회원 정보 */}
            {isGuestOrder && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <TextInput
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="이름을 입력하세요"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">이메일</label>
                  <TextInput
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="이메일을 입력하세요"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">전화번호</label>
                  <TextInput
                    value={guestMobile}
                    onChange={(e) => setGuestMobile(e.target.value)}
                    placeholder="전화번호를 입력하세요"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 추가 정보 카드 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 text-left">추가 정보</h2>

            {/* 결제 상태 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">결제 상태</label>
              <select
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                value={initialStatus}
                onChange={(e) => setInitialStatus(e.target.value as "BEFORE_PAYMENT" | "RECEIVED")}
              >
                <option value="BEFORE_PAYMENT">결제 전</option>
                <option value="RECEIVED">결제 완료</option>
              </select>
            </div>

            {/* 메모 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">메모</label>
              <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="메모를 입력하세요"
              />
            </div>

            {/* 파일 업로드 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">주문 원본 파일</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50 hover:bg-gray-100 transition">
                <input
                  type="file"
                  id="custom-order-file"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setOrderFile(e.target.files[0]);
                    }
                  }}
                />
                <label htmlFor="custom-order-file" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  {orderFile ? (
                    <p className="text-sm text-gray-700 font-medium">{orderFile.name}</p>
                  ) : (
                    <p className="text-sm text-gray-500">파일을 선택하거나 드래그하세요</p>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* 총 금액 카드 */}
          {!withoutPrice && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 text-left">주문 금액</h2>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">상품 금액</span>
                  <span className="font-medium">₩{(productPrice * productCount).toLocaleString()}</span>
                </div>
                {customOptions.map((opt, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-700">{opt.optionName || `옵션 ${idx + 1}`}</span>
                    <span>₩{(opt.optionPrice * opt.optionCount).toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold text-base border-t border-gray-300 pt-2 mt-2">
                  <span>총 금액</span>
                  <span className="text-blue-600">₩{calculateTotal().toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          {/* 제출 버튼 */}
          <div className="flex gap-3">
            <Button
              onClick={handleSubmit}
              disabled={
                !customProductName ||
                (!withoutPrice && productPrice <= 0) ||
                productCount <= 0 ||
                (!isGuestOrder && !selectedMemberId) ||
                (isGuestOrder && !guestName)
              }
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3"
            >
              커스텀 주문 생성
            </Button>
            <Button variant="outline" onClick={onBack} className="px-8">
              취소
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
